import React, { useEffect, useRef, useState } from 'react';
import {
  EmailAuthProvider,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  getMultiFactorResolver,
  reauthenticateWithCredential,
} from 'firebase/auth';
import { auth } from '../firebase';

function obterMensagemErroSenha(error) {
  if (error?.code === 'auth/wrong-password' || error?.code === 'auth/invalid-credential') {
    return 'A senha informada não confere. Revise e tente novamente.';
  }

  if (error?.code === 'auth/too-many-requests') {
    return 'Muitas tentativas seguidas. Aguarde alguns minutos e tente novamente.';
  }

  if (error?.code === 'auth/requires-recent-login') {
    return 'Por segurança, saia e entre novamente antes de repetir esta ação.';
  }

  return 'Não foi possível confirmar sua identidade agora. Tente novamente.';
}

function obterMensagemErroMfa(error) {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  if (error?.code === 'auth/captcha-check-failed') {
    return `O domínio ${hostname || 'deste painel'} ainda não está autorizado no Firebase para SMS. Vá em Authentication > Configurações > Domínios autorizados e adicione este domínio.`;
  }

  if (error?.code === 'auth/too-many-requests') {
    return 'Muitas tentativas seguidas. Aguarde alguns minutos e tente novamente.';
  }

  if (error?.code === 'auth/requires-recent-login') {
    return 'Por segurança, saia e entre novamente antes de repetir esta ação.';
  }

  return 'Não foi possível concluir a confirmação reforçada por SMS agora. Tente novamente.';
}

function ModalReautenticacao({
  aberto,
  titulo,
  descricao,
  textoBotao = 'Confirmar',
  onFechar,
  onConfirmado,
}) {
  const [senha, setSenha] = useState('');
  const [codigoMfa, setCodigoMfa] = useState('');
  const [mfaResolver, setMfaResolver] = useState(null);
  const [mfaVerificationId, setMfaVerificationId] = useState('');
  const [mfaDestino, setMfaDestino] = useState('');
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);
  const recaptchaRef = useRef(null);
  const recaptchaContainerId = 'modal-reauth-recaptcha';

  useEffect(() => {
    if (aberto) {
      return undefined;
    }

    setSenha('');
    setCodigoMfa('');
    setMfaResolver(null);
    setMfaVerificationId('');
    setMfaDestino('');
    setErro('');

    if (recaptchaRef.current) {
      recaptchaRef.current.clear();
      recaptchaRef.current = null;
    }

    return undefined;
  }, [aberto]);

  useEffect(() => () => {
    if (recaptchaRef.current) {
      recaptchaRef.current.clear();
      recaptchaRef.current = null;
    }
  }, []);

  async function prepararRecaptcha() {
    if (recaptchaRef.current) {
      return recaptchaRef.current;
    }

    const verifier = new RecaptchaVerifier(auth, recaptchaContainerId, {
      size: 'invisible',
    });

    await verifier.render();
    recaptchaRef.current = verifier;
    return verifier;
  }

  async function iniciarDesafioMfa(error) {
    const resolver = getMultiFactorResolver(auth, error);
    const hint = resolver.hints.find((item) => item.factorId === PhoneMultiFactorGenerator.FACTOR_ID) || resolver.hints[0];

    if (!hint) {
      throw new Error('Nenhum segundo fator compatível foi encontrado para esta conta.');
    }

    const verifier = await prepararRecaptcha();
    const phoneAuthProvider = new PhoneAuthProvider(auth);
    const verificationId = await phoneAuthProvider.verifyPhoneNumber({
      multiFactorHint: hint,
      session: resolver.session,
    }, verifier);

    setMfaResolver(resolver);
    setMfaVerificationId(verificationId);
    setMfaDestino(hint.phoneNumber || 'celular cadastrado');
    setErro('');
  }

  async function concluirFluxo() {
    try {
      if (typeof onConfirmado === 'function') {
        await onConfirmado();
      }

      if (typeof onFechar === 'function') {
        onFechar();
      }
    } catch (error) {
      console.error('Falha ao concluir ação protegida:', error);
      setErro(error?.message || 'A identidade foi confirmada, mas a ação não pôde ser concluída agora.');
    }
  }

  async function confirmarCodigoMfa() {
    if (!mfaResolver || !mfaVerificationId || codigoMfa.trim().length < 6) {
      setErro('Digite o código recebido por SMS para concluir a confirmação.');
      return;
    }

    setCarregando(true);
    setErro('');

    try {
      const credencial = PhoneAuthProvider.credential(mfaVerificationId, codigoMfa.trim());
      const assertion = PhoneMultiFactorGenerator.assertion(credencial);
      await mfaResolver.resolveSignIn(assertion);
      await auth.currentUser?.getIdToken(true);
      await concluirFluxo();
    } catch (error) {
      console.error('Falha ao confirmar MFA na reautenticação:', error);
      setErro('Código inválido ou expirado. Gere um novo código e tente novamente.');
    } finally {
      setCarregando(false);
    }
  }

  async function confirmarIdentidade() {
    if (mfaResolver) {
      await confirmarCodigoMfa();
      return;
    }

    const usuarioAtual = auth.currentUser;
    if (!usuarioAtual?.email) {
      setErro('Não foi possível identificar o e-mail da conta logada. Entre novamente.');
      return;
    }

    if (!senha.trim()) {
      setErro('Digite sua senha atual para continuar.');
      return;
    }

    setCarregando(true);
    setErro('');

    try {
      const credencial = EmailAuthProvider.credential(usuarioAtual.email, senha);
      await reauthenticateWithCredential(usuarioAtual, credencial);
      await usuarioAtual.getIdToken(true);
      await concluirFluxo();
    } catch (error) {
      if (error?.code === 'auth/multi-factor-auth-required') {
        try {
          await iniciarDesafioMfa(error);
        } catch (mfaError) {
          console.error('Falha ao iniciar MFA na reautenticação:', mfaError);
          setErro(obterMensagemErroMfa(mfaError));
        }
      } else {
        console.error('Falha na reautenticação sensível:', error);
        setErro(obterMensagemErroSenha(error));
      }
    } finally {
      setCarregando(false);
    }
  }

  if (!aberto) {
    return null;
  }

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', zIndex: 9999 }}>
      <div className="panel-compact-dialog" style={{ width: '100%', maxWidth: '440px', backgroundColor: 'white', boxShadow: '0 18px 40px rgba(0,0,0,0.18)' }}>
        <h3 style={{ marginTop: 0, marginBottom: '10px', color: '#1f2937' }}>{titulo}</h3>
        <p style={{ marginTop: 0, marginBottom: '18px', color: '#6b7280', lineHeight: '1.5' }}>{descricao}</p>

        {!mfaResolver ? (
          <>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 'bold', color: '#4b5563' }}>
              Senha atual
            </label>
            <input
              type="password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              disabled={carregando}
              placeholder="Digite sua senha"
              className="routine-input"
              style={{ width: '100%', boxSizing: 'border-box', marginBottom: '14px' }}
            />
          </>
        ) : (
          <>
            <div style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '12px', marginBottom: '14px', fontSize: '13px', color: '#1e3a8a' }}>
              Conta com MFA detectada. Digite o código enviado para {mfaDestino}.
            </div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 'bold', color: '#4b5563' }}>
              Código de segurança
            </label>
            <input
              type="text"
              value={codigoMfa}
              onChange={(e) => setCodigoMfa(e.target.value)}
              disabled={carregando}
              placeholder="000000"
              className="routine-input"
              style={{ width: '100%', boxSizing: 'border-box', marginBottom: '14px' }}
            />
          </>
        )}

        {erro && (
          <p style={{ color: '#b91c1c', backgroundColor: '#fef2f2', border: '1px solid #fecaca', padding: '10px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' }}>
            {erro}
          </p>
        )}

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={confirmarIdentidade}
            disabled={carregando}
            className="routine-btn"
            style={{ flex: '1 1 180px', backgroundColor: '#e53935', color: 'white' }}
          >
            {carregando ? 'Confirmando...' : textoBotao}
          </button>

          <button
            type="button"
            onClick={onFechar}
            disabled={carregando}
            className="routine-btn"
            style={{ flex: '1 1 140px', border: '1px solid #d1d5db', backgroundColor: 'white', color: '#374151' }}
          >
            Voltar
          </button>
        </div>

        <div id={recaptchaContainerId} />
      </div>
    </div>
  );
}

export default ModalReautenticacao;
